import { Base64 } from 'js-base64';
import * as xh from 'xhwebtool';
import { WebCollectEvent, encodeWebCollectEvent } from './proto/proto';
import cookie from './cookie';
import { md5 } from 'js-md5';
/**
 * 将给定的 WebCollectEvent 数据编码为 base64 字符串。
 *
 * @param {WebCollectEvent} eventData - 要编码的 WebCollectEvent 数据。
 * @return {string} base64 编码的数据。
 */
export function encodeProtoData(eventData: WebCollectEvent) {
  const serializedData = encodeWebCollectEvent(eventData);

  const base64Data = Base64.fromUint8Array(serializedData);

  return base64Data;
}
/**
 * 基于用户代理字符串检测设备类型。
 *
 * @return {'pc' | 'mobile'} 设备类型，可以是'pc'或'mobile'。
 */
export function detectDeviceType(): 'pc' | 'mobile' {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any)?.opera;

  // 此正则表达式覆盖了大多数移动设备
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

  if (mobileRegex.test(userAgent)) {
    return 'mobile';
  }

  return 'pc';
}
/**
 * 获取当前用户浏览器信息。
 *
 * @return {string} 以字符串形式表示的浏览器信息。
 */
export function getBrowserInfo(): string {
  const ua = navigator.userAgent;
  const browserName = navigator.appName;
  const fullVersion = `${parseFloat(navigator.appVersion)}`;
  const majorVersion = parseInt(navigator.appVersion, 10);
  const nameOffset = ua.lastIndexOf(' ') + 1;
  const verOffset = ua.indexOf(')');
  const os = navigator.platform;

  let browserInfo = `UserAgent: ${ua}, `;
  browserInfo += `Browser Name: ${browserName}, `;
  browserInfo += `Full Version: ${fullVersion}, `;
  browserInfo += `Major Version: ${majorVersion}, `;
  browserInfo += `OS: ${os}`;

  // 获取更具体的浏览器版本信息
  if (verOffset > -1 && nameOffset < verOffset) {
    browserInfo += `, Browser Details: ${ua.substring(nameOffset, verOffset)}`;
  }

  return browserInfo;
}
/**
 * 根据用户代理字符串获取移动浏览器的名称。
 *
 * @return {string} 移动浏览器的名称。
 */
export function getMobileBrowserName(): string {
  const ua = navigator.userAgent;

  // 检测主流移动浏览器
  if (/CriOS/i.test(ua)) {
    return 'Chrome for iOS';
  }
  if (/FxiOS/i.test(ua)) {
    return 'Firefox for iOS';
  }
  if (/OPiOS/i.test(ua)) {
    return 'Opera for iOS';
  }
  if (/Opera Mini/i.test(ua)) {
    return 'Opera Mini';
  }
  if (/UCBrowser/i.test(ua)) {
    return 'UC Browser';
  }
  if (/SamsungBrowser/i.test(ua)) {
    return 'Samsung Internet';
  }
  if (/Silk\//i.test(ua)) {
    return 'Amazon Silk';
  }
  if (/Chrome/i.test(ua) && /Android/i.test(ua)) {
    return 'Chrome for Android';
  }
  if (/Firefox\/\d+/i.test(ua) && /Android/i.test(ua)) {
    return 'Firefox for Android';
  }
  if (/Version\/\d+.*Safari/i.test(ua) && /Mobile\/\w+.*Safari/i.test(ua)) {
    return 'Safari for iOS';
  }
  if (/MSIE|Trident/i.test(ua)) {
    return 'Internet Explorer Mobile';
  }
  if (/Edge\/\d+/i.test(ua)) {
    return 'Microsoft Edge for Mobile';
  }
  if (navigator.appName) {
    return navigator.appName;
  }
  return 'unknown';
}
/**
 * 从用户代理字符串中获取移动浏览器的版本。
 *
 * @return {string} 移动浏览器的版本，如果不是已知的浏览器，则返回"unknown"。
 */
export function getMobileBrowserVersion(): string {
  const ua = navigator.userAgent;

  // 定义一系列的正则表达式，用于匹配主流移动浏览器及其版本号
  const browserRegexes = [
    /(?:firefox|fxios)\/(\d+(\.\d+)?)/i, // Firefox
    /(?:chrome|crios)\/(\d+(\.\d+)?)/i, // Chrome
    /version\/(\d+(\.\d+)?).*safari/i, // Safari
    /opr\/(\d+(\.\d+)?)/i, // Opera
    /edg(?:e|ios|a)?\/(\d+(\.\d+)?)/i, // Edge
    /samsungbrowser\/(\d+(\.\d+)?)/i, // Samsung Internet
    /ucbrowser\/(\d+(\.\d+)?)/i, // UC Browser
  ];
  for (let index = 0; index < browserRegexes.length; index += 1) {
    const regex = browserRegexes[index];
    const match = ua.match(regex);
    if (match) {
      return `${match[0]} (Version: ${match[1]})`;
    }
  }

  // 如果没有匹配到任何已知浏览器，则返回"unknown"
  return 'unknown';
}
/**
 * 返回当前网站的域名。
 *
 * @return {string} 当前域名。
 */
export function getCurrentDomain(): string {
  return window.location.hostname;
}
/**
 * 根据本地存储中特定标识的存在来检查用户是否是新用户。
 *
 * @return {boolean} 如果用户是新用户，则返回true；如果用户不是新用户，则返回false。
 */
export function checkIfNewUser(): boolean {
  // 检查localStorage中的标识
  const userStatus = cookie.getCookie('ra_event_tracker_isNewUser');

  if (userStatus === '') {
    // 如果标识不存在，设置标识，并认为这是新用户
    cookie.setCookie('ra_event_tracker_isNewUser', 'true', 0); // 将用户标记为老用户，因为下次访问不再是新用户
    return true; // 是新用户
  }

  // 如果标识存在，认为这是老用户
  return false; // 不是新用户
}
/**
 * 获取设备ID。
 *
 * @return {Promise<string>} 设备ID。
 */
export async function getDeviceId() {
  let deviceId = '';
  deviceId = cookie.getCookie('ra_event_tracker_deviceId');
  if (deviceId === '') {
    deviceId = await xh.Fingerprint.fingerprint();
    cookie.setCookie('ra_event_tracker_deviceId', deviceId, 0); // 将用户标记为老用户，因为下次访问不再是新用户
  }

  return deviceId;
}

/**
 * 设置运行ID。
 *
 * @return {Promise<string>} 运行ID。
 */
export function setRunId() {
  const runId = md5(`runId-${Date.now()}-${Math.random()}`);

  cookie.setCookie('ra_event_tracker_runId', runId, 0);

  return runId;
}
/**
 * 获取运行ID。
 *
 * @return {Promise<string>} 运行ID。
 */
export function getRunId() {
  let runId = cookie.getCookie('ra_event_tracker_runId');
  if (runId === '') {
    runId = setRunId();
  }

  return runId;
}
